import moment from "moment";
import DOMPurify from 'dompurify';
import _reverse from "lodash/reverse";
import _clone from "lodash/clone";

/**
 * Utilities related to data formatting
 */

/**
 * Format a number to the usual format e.g. 1.3B for 1,300,000,000
 * @param {number} number
 * @returns {string}
 */
export const formatNumber = (number) => {
    let stringValue;
    if(number < 1000){ // (0 - 1k)
        stringValue = number.toLocaleString();
    } else if (number < 1000000){ // (1k - 1m)
        stringValue = (number / 1000).toFixed(1).toLocaleString() + "K";
    } else if (number < 1000000000){ // (1m - 1b)
        stringValue = (number / 1000000).toFixed(1).toLocaleString() + "M";
    } else { // (1b +)
        stringValue = (number / 1000000000).toFixed(1).toLocaleString() + "B";
    }
    return stringValue;
};


/**
 * Replace newlines (\n) with <br />
 * @param {string} content
 * @returns {string} HTML
 */
export const replaceNewLines = (content = "") => {
    return content.replace(/\n/g, "<br />");
};


/**
 * Prepare a piece of content in HTML to be shown
 * Purify HTML
 * Replace newlines (\n) with <br />
 * @param {string} content
 * @returns {string} HTML
 */
export const prepareForHTML = (content = "") => {
    return DOMPurify.sanitize(replaceNewLines(content));
};

/**
 * Prepare content to be shown but doesn't strip target attribute
 * @param {string} content
 * @returns {string}
 */
export const prepareExcerptForHTML = (content = "") => {
    return DOMPurify.sanitize(replaceNewLines(content), {ADD_ATTR: ["target"]});
};

export const convertAndFormatUTC = (utcLong, pattern = "MM-DD-YYYY", offsetHours = -4) => {
    if (utcLong === null || utcLong === undefined) {
        return "";
    }

    const initalDate = moment.utc(utcLong);

    const offsetDate = initalDate.utcOffset(offsetHours);

    return offsetDate.format(pattern);
};

/**
 * "Fix" the provided string value (if the value is "DEFAULT", make it "" instead)
 * @param value
 * @returns {string}
 */
export const fixValue = (value) => {
    const isDefault = value && (value.toUpperCase() == "DEFAULT");
    return isDefault ? "" : value;
};

/**
 * create a link to a newsletter, adding anchor if necessary
 * @param  {String} accessKey
 * @param  {String|Number} groupingId
 * @param  {String} date
 * @param  {String} hash
 * @param  {String|Number} urlTemporaryId
 * @return {String}
 */
export const getFormattedArchiveLink = (accessKey, groupingId, date, hash, urlTemporaryId) => {
    const hashPortion = (hash && hash !== "") ? `#${hash}` : "";

    let typePortion = "archive";

    let tempPortion = "";

    if (urlTemporaryId && urlTemporaryId !== "") {
        typePortion = "temporary";

        tempPortion = `/${urlTemporaryId}`;
    }

    if (accessKey && groupingId && date) {
        return `/${accessKey}/newsletter/${groupingId}/${typePortion}/${date}${tempPortion}${hashPortion}`;
    } else {
       return null;
    }
};

/**
 * hack to support "associcated articles". instead of being parsed on the BE these are idenitfied
 * as being "associated" to a previous article in the section by adding "associated" in the comment
 * field.
 *
 * associated articles are not output like other articles; they are just listed following the
 * "top level" article they are associated with.
 *
 * instead of handling these articles in the various places they are output, we format the data as
 * far upstream as possible, thus limiting the places the code needs to check for their existance,
 * and allowing us to remove this bit easily once the BE does the heavy lifting of sending the
 * data in the correct format in the first place
 *
 * unfortunately both sections & subsections might have associated articles, so both are
 * processed here
 *
 * @param  {} responseData - the service response
 * @return {} - data parsed for associated articles
 */
export const formatResponseData = (responseData) => {
    responseData.data.sectionList.forEach(section => {
        section.giList = extractAssociatedArticles(section.giList, responseData.data.giData);

        const subsections = section.subSections;

        if (subsections) {
            subsections.forEach(subSection => {
                subSection.giList = extractAssociatedArticles(
                    subSection.giList,
                    responseData.data.giData
                );
            });
        }
    });

    return responseData;
}

/**
 * remove associated from (sub)section article lists; becomes data under the corresponding article
 * @return {Array} - remaining (sub)section items to output.
 */
const extractAssociatedArticles = (giList, giData) => {
    let associatedGiList = [];

    const remainingItems = _reverse(_clone(giList)).filter((giKey, index) => {
        const article = giData[giKey];

        const comment = article.comment || "";

        const isAssociated = comment.toUpperCase() === "ASSOCIATED";

        //note first item in a section (here check for last b/c we are reversed) can't be "associated"
        if (isAssociated && !(index === giList.length - 1)) {
            associatedGiList.unshift(giKey);

            return false;
        } else {
            article.associatedGiList = associatedGiList;

            associatedGiList = [];

            return true;
        }
    });

    return _reverse(remainingItems);
}
